import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons/faEnvelope';

import { MessageThreadCard } from 'us-react-components/dist/MessageThreadCard/MessageThreadCard';
import {
  UserNameFilter,
  UserNameFilterOption,
} from 'us-react-components/dist/UserNameFilter/UserNameFilter';
import { MessageThread, MessageThreadService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const MAX_PREVIEW = 100;

const truncate = (s?: string, n: number = MAX_PREVIEW): string => {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

export const Messages = observer((): JSX.Element => {
  const { t } = useTranslation();
  const { id: pathId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [threads, setThreads] = useState<{
    data: MessageThread[];
    meta?: { size?: number; pageCount?: number };
  }>({ data: [] });
  const [isLoading, setIsLoading] = useState(true);

  const page = Number(searchParams.get('page') || 1) || 1;
  const sitterIdParam = searchParams.get('sitterId');


  useEffect(() => {
    document.title = 'UrbanSitter - Messages';
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const parentId = Number(pathId) || user.id;
    if (parentId !== user.id) {
      navigate(`/parent/${user.id}/messages`);
      return;
    }
    const params: Record<string, unknown> = {
      page,
      parentId,
      userId: user.id,
      sort: 'modified',
      'modified.dir': 'desc',
    };
    if (sitterIdParam) params.sitterId = Number(sitterIdParam);

    setIsLoading(true);
    const load = async (): Promise<void> => {
      try {
        const resp = await MessageThreadService.getByFilter({ params });
        setThreads({
          data: resp.data?.data ?? [],
          meta: resp.data?.meta,
        });
      } catch (err) {
        console.warn('[Messages] fetch threads failed', err);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, [user?.id, pathId, page, sitterIdParam]);

  const setPage = (next: number): void => {
    setSearchParams((sp) => {
      const n = new URLSearchParams(sp);
      n.set('page', String(next));
      return n;
    }, { replace: true });
  };

  const onFilterUserChange = (u: UserNameFilterOption | null): void => {
    setSearchParams(
      (sp) => {
        const n = new URLSearchParams(sp);
        if (u?.id) n.set('sitterId', String(u.id));
        else n.delete('sitterId');
        n.set('page', '1');
        return n;
      },
      { replace: true },
    );
  };

  const totalPages = threads.meta?.pageCount ?? 1;
  const hasAny = threads.data.length > 0;

  return (
    <div className='container py-8'>
      <h1 className='text-2xl font-semibold mb-4'>{t('pages.messages.heading')}</h1>

      <div className='grid grid-cols-1 sm:grid-cols-12 gap-4 mb-5'>
        <div className='sm:col-span-6 md:col-span-4'>
          <UserNameFilter
            kind='sitter'
            viewerId={user?.id}
            label={t('common.caregiverLabel')}
            onSelect={onFilterUserChange}
          />
        </div>
      </div>

      {isLoading && (
        <h2 className='text-base'>
          <i className='fa-solid fa-spinner fa-spin mr-2' />
          {t('pages.messages.loading')}
        </h2>
      )}

      {!isLoading && !hasAny && (
        <div className='text-center max-w-lg mx-auto py-8'>
          <FontAwesomeIcon icon={faEnvelope} className='text-5xl text-neutral-400 mb-4' aria-hidden='true' />
          <h4 className='font-semibold mb-2'>{t('pages.messages.emptyHeading')}</h4>
          <div>
            <a id='search-for-sitters' href='/app/sitters' className='underline'>
              {t('pages.messages.emptySearchPrefix')}
            </a>{' '}
            {t('pages.messages.emptyMiddle')}{' '}
            <a id='post-a-job' href='/app/job-post' className='underline'>
              {t('pages.messages.emptyPostLink')}
            </a>{' '}
            {t('pages.messages.emptyAfter')}
          </div>
        </div>
      )}

      {!isLoading && hasAny && (
        <div className='max-w-4xl'>
          <ul className='space-y-3 list-none p-0'>
            {threads.data.map((thread) => {
              const sitter = thread.sitter;
              const parent = thread.parent;
              const lastMessage = thread.lastMessage;
              const preview = truncate(lastMessage?.message) || t('pages.messages.startConversation');
              return (
                <li key={thread.id}>
                  <MessageThreadCard
                    id={thread.id}
                    href={`/app/parent/${parent?.id}/messages/${sitter?.id}`}
                    unread={!thread.read}
                    counterpartyName={sitter?.firstName}
                    counterpartyAvatarUrl={sitter?.pictureUrl}
                    label={lastMessage?.label}
                    preview={preview}
                    hasMessageBody={!!lastMessage?.message}
                  />
                </li>
              );
            })}
          </ul>

          {totalPages > 1 && (
            <div className='flex items-center justify-center gap-3 mt-6'>
              <Button
                buttonType={ButtonType.secondary}
                disabled={page <= 1}
                onClick={(): void => setPage(page - 1)}
              >
                {t('common.previous')}
              </Button>
              <span className='text-sm text-neutral-700'>Page {page} of {totalPages}</span>
              <Button
                buttonType={ButtonType.secondary}
                disabled={page >= totalPages}
                onClick={(): void => setPage(page + 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
